import { computed, ref, toRaw, toValue, watch, type MaybeRefOrGetter, type Ref } from "vue";
import type { JsonValue } from "@/src/design_system/shared/types/jsonValue";
import { readValidatedJsonEntry, writeValidatedJsonEntry } from "@/src/shared/utils/localStorage";

export const usePersistentPreference = <T extends JsonValue>({
  storageKey,
  key,
  value,
  validate,
}: {
  storageKey: string;
  key: MaybeRefOrGetter<string | undefined>;
  value: Ref<T>;
  validate: (value: unknown) => value is T;
}) => {
  const entryKey = toValue(key);

  if (typeof entryKey !== "string" || entryKey.trim() === "") {
    return value;
  }

  const restored = readValidatedJsonEntry(storageKey, entryKey, { validate, fallback: value.value });
  let previous = toRaw(restored);
  value.value = restored;

  // A model's restored value can return through its parent on the next render.
  // Do not save that handoff, including a raw-to-proxy conversion.
  watch(
    () => toRaw(value.value),
    nextValue => {
      if (Object.is(nextValue, previous)) {
        return;
      }
      previous = nextValue;
      writeValidatedJsonEntry(storageKey, entryKey, { value: nextValue, validate });
    }
  );

  return useInitialModelValue(value, restored);
};

const useInitialModelValue = <T>(model: Ref<T>, initialValue: T): Ref<T> => {
  const modelAlreadyInitialized = Object.is(toRaw(model.value), toRaw(initialValue));

  if (modelAlreadyInitialized) {
    return model;
  }

  const hasModelUpdated = ref(false);

  // Until the parent updates its model, render the resolved initial preference.
  watch(
    model,
    () => {
      hasModelUpdated.value = true;
    },
    { once: true, flush: "sync" }
  );

  return computed({
    get: () => (hasModelUpdated.value ? model.value : initialValue),
    set: nextValue => {
      model.value = nextValue;
    },
  });
};
